import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const RED       = '#F87171';
const FONT_BODY = "'DM Sans', sans-serif";

const modalField = {
  width: '100%', boxSizing: 'border-box',
  backgroundColor: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '8px', padding: '9px 12px',
  color: WHITE, fontFamily: FONT_BODY, fontSize: '14px',
};

function formatTime12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 22; h++) {
    const minutes = h === 22 ? [0] : [0, 15, 30, 45];
    for (const m of minutes) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      opts.push({ value, label: formatTime12h(value) });
    }
  }
  return opts;
})();

function statusMeta(status, t) {
  if (status === 'accepted')  return { label: t('checkin.statusConfirmed'), color: GREEN };
  if (status === 'declined')  return { label: t('checkin.statusDeclined'), color: RED };
  if (status === 'completed') return { label: t('checkin.statusCompleted'), color: 'rgba(255,255,255,0.5)' };
  if (status === 'cancelled') return { label: t('checkin.statusCancelled'), color: 'rgba(255,255,255,0.35)' };
  return { label: t('checkin.statusPending'), color: AMBER };
}

// Minimal, dedicated request/accept panel for Check-in Calls -- Organization staff <-> a
// genuinely connected worker (via organization_members), unrelated to job interviews.
// Mirrors the interview propose/accept UX in Messaging.jsx, but as its own standalone
// component (not a Messaging.jsx change) since check-ins don't need full chat threads --
// see PR discussion. RLS on organization_checkins already enforces the connection and the
// 30-minute cap; this component just can't submit anything outside those bounds.
//
// `viewerType`: 'organization' | 'worker'.
// `viewerId`: the org staff member's or worker's own auth id.
// `organizationId`: required when viewerType === 'organization'.
// `counterparts`: [{ id, name }] -- connected workers (org side) or connected
//   organizations (worker side) that a new request can be sent to/from.
// hideNoCounterpartsNotice: the caller shows its own empty state (worker Check-ins tab).
export default function CheckInPanel({ viewerType, viewerId, organizationId, counterparts, isMobile, hideNoCounterpartsNotice = false }) {
  const { t } = useTranslation();
  const [checkins, setCheckins] = useState([]);
  const [loaded, setLoaded]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ counterpartId: '', date: '', time: '', duration: '15', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [fallbackNames, setFallbackNames] = useState({});

  useEffect(() => {
    if (viewerType === 'organization' && !organizationId) return;
    if (viewerType === 'worker' && !viewerId) return;
    let cancelled = false;
    (async () => {
      const base = supabase.from('organization_checkins').select('*').order('scheduled_at', { ascending: true });
      const { data } = viewerType === 'organization'
        ? await base.eq('organization_id', organizationId)
        : await base.eq('worker_id', viewerId);
      if (!cancelled) { setCheckins(data || []); setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [viewerType, organizationId, viewerId]);

  // A check-in's counterpart can fall out of `counterparts` (e.g. an organization_members row
  // was removed after the check-in was created) while the check-in itself still exists --
  // `counterparts` alone would then show "Unknown" forever. Back-fill those specific ids with
  // a direct lookup so historical check-ins stay identifiable.
  useEffect(() => {
    if (!checkins.length) return;
    const knownIds = new Set(counterparts.map(c => c.id));
    const missingIds = [...new Set(
      checkins
        .map(ci => viewerType === 'organization' ? ci.worker_id : ci.organization_id)
        .filter(id => id && !knownIds.has(id) && !(id in fallbackNames))
    )];
    if (missingIds.length === 0) return;
    let cancelled = false;
    (async () => {
      if (viewerType === 'organization') {
        const { data } = await supabase.from('workers').select('id, first_name, last_name').in('id', missingIds);
        if (cancelled) return;
        const map = Object.fromEntries((data || []).map(w => [w.id, `${w.first_name || ''} ${w.last_name || ''}`.trim()]));
        setFallbackNames(prev => ({ ...prev, ...map }));
      } else {
        const { data } = await supabase.from('organizations').select('id, org_name').in('id', missingIds);
        if (cancelled) return;
        const map = Object.fromEntries((data || []).map(o => [o.id, o.org_name]));
        setFallbackNames(prev => ({ ...prev, ...map }));
      }
    })();
    return () => { cancelled = true; };
  }, [checkins, counterparts, viewerType]); // eslint-disable-line

  // On the worker's side the counterpart is the organization, which says nothing about WHO
  // they're actually meeting. requested_by identifies the individual staff member, and members
  // can now read their org's active staff (name + photo), so resolve it and show the person.
  const [staffById, setStaffById] = useState({});
  useEffect(() => {
    if (viewerType !== 'worker') return;
    const orgIds = [...new Set(checkins.map(ci => ci.organization_id).filter(Boolean))];
    if (orgIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('organization_staff')
        .select('user_id, display_name, avatar_url')
        .in('organization_id', orgIds);
      if (cancelled) return;
      setStaffById(Object.fromEntries((data || []).map(r => [r.user_id, r])));
    })();
    return () => { cancelled = true; };
  }, [checkins, viewerType]);

  const staffFor = (ci) => (viewerType === 'worker' ? staffById[ci.requested_by] : null);

  const counterpartName = (ci) => {
    const cid = viewerType === 'organization' ? ci.worker_id : ci.organization_id;
    return counterparts.find(c => c.id === cid)?.name || fallbackNames[cid] || t('checkin.unknownCounterpart');
  };

  const handleRequest = async () => {
    const { counterpartId, date, time, duration, notes } = form;
    if (!counterpartId || !date || !time) return;
    setSubmitting(true);
    setErrorMsg('');
    const scheduled_at = new Date(`${date}T${time}:00`).toISOString();
    const payload = viewerType === 'organization'
      ? { organization_id: organizationId, worker_id: counterpartId, requested_by: viewerId, scheduled_at, duration_minutes: Number(duration), notes: notes.trim() || null }
      : { organization_id: counterpartId, worker_id: viewerId, requested_by: viewerId, scheduled_at, duration_minutes: Number(duration), notes: notes.trim() || null };

    const { data, error } = await supabase.from('organization_checkins').insert(payload).select().single();
    setSubmitting(false);
    if (error) { setErrorMsg(t('checkin.couldNotRequestPrefix') + error.message); return; }
    setCheckins(prev => [...prev, data].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    setShowForm(false);
    setForm({ counterpartId: '', date: '', time: '', duration: '15', notes: '' });
  };

  const handleRespond = async (id, status) => {
    setBusyId(id);
    setErrorMsg('');
    const updates = { status };

    if (status === 'accepted') {
      try {
        const res = await fetch('/api/create-checkin-room', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checkinId: id }),
        });
        const room = await res.json();
        if (!res.ok) { setErrorMsg(t('checkin.couldNotCreateRoomPrefix') + (room.error || t('checkin.unknownError'))); setBusyId(null); return; }
        updates.meeting_link = room.url;
        updates.accepted_at = new Date().toISOString();
      } catch (err) {
        setErrorMsg(t('checkin.somethingWrongCreatingRoomPrefix') + err.message);
        setBusyId(null);
        return;
      }
    }

    const { data, error } = await supabase.from('organization_checkins').update(updates).eq('id', id).select().single();
    setBusyId(null);
    if (error) { setErrorMsg(t('checkin.couldNotUpdatePrefix') + error.message); return; }
    setCheckins(prev => prev.map(c => (c.id === id ? data : c)));
  };

  const openCheckin = (ci) => {
    if (!ci.meeting_link) return;
    window.open(`/checkin?checkinId=${ci.id}`, '_blank', 'noopener,noreferrer');
  };

  const hasCounterparts = counterparts.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>
          {t('checkin.panelHeading')}
        </p>
        {hasCounterparts && (
          <button
            onClick={() => setShowForm(v => !v)}
            style={{
              backgroundColor: AMBER, color: '#0D1B2A', border: 'none', borderRadius: '20px',
              padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer',
            }}
          >
            {showForm ? t('checkin.cancelRequestButton') : t('checkin.requestCheckinButton')}
          </button>
        )}
      </div>

      {!hasCounterparts && !hideNoCounterpartsNotice && (
        <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '18px', color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '16px' }}>
          {viewerType === 'organization' ? t('checkin.noConnectedMembers') : t('checkin.noConnectedOrganizations')}
        </div>
      )}

      {errorMsg && (
        <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', padding: '10px 14px', color: RED, fontSize: '13px', marginBottom: '14px' }}>
          {errorMsg}
        </div>
      )}

      {showForm && (
        <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, display: 'block', marginBottom: '6px' }}>
              {viewerType === 'organization' ? t('checkin.withMemberLabel') : t('checkin.withOrganizationLabel')}
            </label>
            <select value={form.counterpartId} onChange={e => setForm(f => ({ ...f, counterpartId: e.target.value }))} style={modalField}>
              <option value="">{t('checkin.selectCounterpart')}</option>
              {counterparts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, display: 'block', marginBottom: '6px' }}>
                {t('checkin.dateLabel')}
              </label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={{ ...modalField, colorScheme: 'dark' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, display: 'block', marginBottom: '6px' }}>
                {t('checkin.timeLabel')}
              </label>
              <select value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} style={modalField}>
                <option value="">{t('checkin.selectATime')}</option>
                {TIME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, display: 'block', marginBottom: '6px' }}>
                {t('checkin.durationLabel')}
              </label>
              <select value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} style={modalField}>
                <option value="15">{t('checkin.duration15')}</option>
                <option value="30">{t('checkin.duration30')}</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, display: 'block', marginBottom: '6px' }}>
              {t('checkin.notesLabel')} <span style={{ color: 'rgba(255,255,255,0.3)' }}>{t('worker.signup.step4.optional')}</span>
            </label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder={t('checkin.notesPlaceholder')} style={{ ...modalField, resize: 'vertical', lineHeight: 1.5 }} />
          </div>

          <button
            onClick={handleRequest}
            disabled={!(form.counterpartId && form.date && form.time) || submitting}
            style={{
              padding: '11px', borderRadius: '8px', border: 'none',
              backgroundColor: (!(form.counterpartId && form.date && form.time) || submitting) ? 'rgba(245,166,35,0.4)' : AMBER,
              color: '#0D1B2A', fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700,
              cursor: (!(form.counterpartId && form.date && form.time) || submitting) ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? t('checkin.sendingEllipsis') : t('checkin.sendRequestButton')}
          </button>
        </div>
      )}

      {!loaded ? (
        <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('organization.dashboard.shared.loading')}</p>
      ) : checkins.length === 0 ? (
        <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
          {t('checkin.emptyState')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {checkins.map(ci => {
            const when = new Date(ci.scheduled_at);
            const shortDate = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const timeLabel = when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            const meta = statusMeta(ci.status, t);
            // For an organization viewer, "my side requested this" means ANY staff member of
            // the org sent it (requested_by is an individual staff uid, not a shared org id) --
            // comparing against just this viewer's own uid would wrongly show Accept/Decline to
            // a colleague when someone else on their team was the one who actually sent it.
            // Workers have no such multi-seat concept, so a direct id comparison is correct there.
            const iRequested = viewerType === 'organization'
              ? ci.requested_by !== ci.worker_id
              : ci.requested_by === viewerId;
            const needsMyResponse = ci.status === 'pending' && !iRequested;
            const busy = busyId === ci.id;

            return (
              <div key={ci.id} style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between', gap: isMobile ? '8px' : '10px',
                backgroundColor: NAVY_MID, borderRadius: '8px', padding: '14px 16px',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    {staffFor(ci)?.avatar_url && (
                      <img
                        src={staffFor(ci).avatar_url}
                        alt=""
                        style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <p style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE, margin: 0 }}>{counterpartName(ci)}</p>
                  </div>
                  {staffFor(ci)?.display_name && (
                    <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', marginBottom: '2px' }}>
                      {t('checkin.withStaffMember', { name: staffFor(ci).display_name })}
                    </p>
                  )}
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginBottom: '2px' }}>
                    {shortDate} · {timeLabel} · {t('checkin.durationMinutes', { count: ci.duration_minutes })}
                  </p>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color }}>
                    {needsMyResponse ? t('checkin.awaitingYourResponse') : meta.label}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {needsMyResponse && (
                    <>
                      <button
                        onClick={() => handleRespond(ci.id, 'accepted')}
                        disabled={busy}
                        style={{ backgroundColor: AMBER, color: '#0D1B2A', border: 'none', borderRadius: '16px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
                      >
                        {t('checkin.accept')}
                      </button>
                      <button
                        onClick={() => handleRespond(ci.id, 'declined')}
                        disabled={busy}
                        style={{ backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '16px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
                      >
                        {t('checkin.decline')}
                      </button>
                    </>
                  )}
                  {ci.status === 'pending' && iRequested && (
                    <button
                      onClick={() => handleRespond(ci.id, 'cancelled')}
                      disabled={busy}
                      style={{ backgroundColor: 'transparent', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '16px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, fontFamily: FONT_BODY, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
                    >
                      {t('checkin.cancelButton')}
                    </button>
                  )}
                  {ci.status === 'accepted' && ci.meeting_link && (
                    <button
                      onClick={() => openCheckin(ci)}
                      style={{ backgroundColor: AMBER, color: '#0D1B2A', border: 'none', borderRadius: '16px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }}
                    >
                      {t('checkin.joinCallButton')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
